import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Ship, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { DelimaLogo } from '../components/DelimaLogo';

const LoginPage = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [nome, setNome] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        let result;
        if (isLogin) {
            result = await login(email, senha);
        } else {
            result = await register(nome, email, senha);
        }

        setLoading(false);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Panel - Background Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1510566846936-a7bfe9d6ae1b?q=80&w=2070&auto=format&fit=crop)'
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 to-slate-950/70" />
                <div className="relative z-10 flex flex-col justify-center p-12">
                    <div className="mb-8">
                        <DelimaLogo variant="login" />
                    </div>
                    
                    <h2 className="font-heading text-4xl font-bold text-white mb-4 tracking-tight">
                        Sala de Monitoramento
                    </h2>
                    <p className="text-slate-400 text-lg max-w-md leading-relaxed">
                        Centralize o monitoramento das operações navais com segurança e conformidade.
                    </p>
                    
                    <div className="mt-12 grid grid-cols-2 gap-6">
                        <div className="p-4 bg-slate-900/50 border border-slate-800 rounded-sm">
                            <Ship className="w-6 h-6 text-cyan-400 mb-2" />
                            <p className="text-sm font-mono text-slate-300">Monitoramento em Tempo Real</p>
                        </div>
                        <div className="p-4 bg-slate-900/50 border border-slate-800 rounded-sm">
                            <AlertCircle className="w-6 h-6 text-amber-400 mb-2" />
                            <p className="text-sm font-mono text-slate-300">Gestão de Ocorrências</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-950">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex justify-center mb-8">
                        <DelimaLogo variant="mobile" />
                    </div>

                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                        <CardHeader className="space-y-1">
                            <CardTitle className="font-heading text-2xl text-white tracking-tight">
                                {isLogin ? 'Entrar no Sistema' : 'Criar Conta'}
                            </CardTitle>
                            <CardDescription className="text-slate-400">
                                {isLogin 
                                    ? 'Digite suas credenciais para acessar' 
                                    : 'Preencha os dados para criar sua conta'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {!isLogin && (
                                    <div className="space-y-2">
                                        <Label htmlFor="nome" className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome Completo
                                        </Label>
                                        <Input
                                            id="nome"
                                            data-testid="input-nome"
                                            type="text"
                                            value={nome}
                                            onChange={(e) => setNome(e.target.value)}
                                            placeholder="Seu nome"
                                            required={!isLogin}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm placeholder:text-slate-600"
                                        />
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        E-mail
                                    </Label>
                                    <Input
                                        id="email"
                                        data-testid="input-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="seu@email.com"
                                        required
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm placeholder:text-slate-600"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="senha" className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Senha
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="senha"
                                            data-testid="input-senha"
                                            type={showPassword ? 'text' : 'password'}
                                            value={senha}
                                            onChange={(e) => setSenha(e.target.value)}
                                            placeholder="••••••••"
                                            required
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm placeholder:text-slate-600 pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 bg-red-950/50 border border-red-900/50 rounded-sm text-red-400 text-sm flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    data-testid="btn-submit"
                                    disabled={loading}
                                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium uppercase tracking-wide text-xs h-10 rounded-sm glow-cyan"
                                >
                                    {loading ? 'Aguarde...' : isLogin ? 'Entrar' : 'Criar Conta'}
                                </Button>
                            </form>

                            <div className="mt-6 text-center">
                                <button
                                    type="button"
                                    data-testid="btn-toggle-mode"
                                    onClick={() => {
                                        setIsLogin(!isLogin);
                                        setError('');
                                    }}
                                    className="text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                                >
                                    {isLogin 
                                        ? 'Não tem conta? Criar agora' 
                                        : 'Já tem conta? Entrar'}
                                </button>
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-xs text-slate-600 mt-6 font-mono">
                        SISTEMA INTEGRADO DE MONITORAMENTO v1.0
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
